import axios from 'axios';
import { getApiEndpoint } from '@/utils/ERPUtils';

import { logger } from '@/utils/logger';
/**
 * ERPNext API Response Wrapper
 */
interface ERPApiResponse<T = any> {
  message: {
    status: 'success' | 'error';
    message?: string;
    data: T;
  };
}

/**
 * Streaming message categories supported by the backend
 */
export type StreamingMessageCategory =
  | 'Upload'
  | 'Create Project'
  | 'Design Generation'
  | 'Mass Planting'
  | 'Video Generation'
  | 'Design Edit'
  | 'Property Analysis'
  | 'General Chat';

/**
 * Streaming Messages grouped by category
 */
export interface StreamingMessagesData {
  [category: string]: string[];
}

/**
 * Streaming Messages Service
 *
 * Fetches configurable streaming/status messages from ERPNext backend.
 * These messages are displayed during AI generation, video processing,
 * file uploads, and other async operations.
 */
class StreamingMessagesService {
  private cache: Map<string, { data: StreamingMessagesData; timestamp: number; expiry: number }> = new Map();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Get all streaming messages grouped by category
   *
   * @returns Promise with messages grouped by category
   */
  async getAllMessages(): Promise<StreamingMessagesData> {
    const cacheKey = 'all_messages';

    // Check cache first
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() < cached.expiry) {
      return cached.data;
    }

    try {
      const response = await axios.get<ERPApiResponse<StreamingMessagesData>>(
        getApiEndpoint('GET_STREAMING_MESSAGES'),
        { withCredentials: true }
      );

      if (response.data?.message?.status === 'success') {
        const data = response.data.message.data;

        // Cache the results
        this.cache.set(cacheKey, {
          data,
          timestamp: Date.now(),
          expiry: Date.now() + this.CACHE_DURATION
        });

        return data;
      } else {
        logger.error('[StreamingMessagesService] API error:', response.data?.message?.message);
        throw new Error(response.data?.message?.message || 'Failed to fetch messages');
      }
    } catch (error) {
      logger.error('[StreamingMessagesService] Request failed:', error);
      throw error;
    }
  }

  /**
   * Get streaming messages for a specific category
   *
   * @param category - The category to fetch messages for
   * @returns Promise with array of messages
   */
  async getMessagesByCategory(category: StreamingMessageCategory): Promise<string[]> {
    const cacheKey = `category_${category}`;

    // Check cache first
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() < cached.expiry) {
      return cached.data[category] || [];
    }

    try {
      const response = await axios.post<ERPApiResponse<{ category: string; messages: string[] }>>(
        getApiEndpoint('GET_STREAMING_MESSAGES_BY_CATEGORY'),
        { category },
        {
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (response.data?.message?.status === 'success') {
        const messages = response.data.message.data.messages;

        // Cache the results
        this.cache.set(cacheKey, {
          data: { [category]: messages },
          timestamp: Date.now(),
          expiry: Date.now() + this.CACHE_DURATION
        });

        return messages;
      } else {
        logger.error('[StreamingMessagesService] API error:', response.data?.message?.message);
        throw new Error(response.data?.message?.message || 'Failed to fetch messages');
      }
    } catch (error) {
      logger.error('[StreamingMessagesService] Request failed:', error);
      throw error;
    }
  }

  /**
   * Clear the cache
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Clear cache for a specific key
   */
  clearCacheEntry(key: string): void {
    this.cache.delete(key);
  }
}

// Export singleton instance
export const streamingMessagesService = new StreamingMessagesService();
